import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function extractUrls(text: string): string[] {
  if (!text) return [];
  
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const matches = text.match(urlRegex);
  
  if (!matches) return [];
  
  // Filter out duplicate URLs and ensure they're valid
  return [...new Set(matches)].filter(url => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  });
}

export function formatDate(date: string | Date): string {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

function getPersistedCurrency(): string {
  if (typeof window === 'undefined') {
    return 'USD';
  }

  try {
    const raw = window.localStorage.getItem('currency-storage');
    if (!raw) return 'USD';

    const parsed = JSON.parse(raw) as { state?: { currency?: string } };
    const selected = parsed?.state?.currency;
    if (typeof selected === 'string' && selected.trim().length > 0) {
      return selected.toUpperCase();
    }
  } catch {
    // ignore malformed storage and keep default
  }

  return 'USD';
}

export function formatCurrency(amount: number, currency?: string): string {
  const activeCurrency = (currency || getPersistedCurrency()).toUpperCase();
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: activeCurrency
  }).format(amount);
}

export function formatCompactNumber(num: number): string {
  return Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1
  }).format(num);
}

export function formatToSafeIso(value: unknown): string {
  if (!value) return new Date().toISOString();
  if (typeof value === 'string') {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d.toISOString();
    return new Date().toISOString();
  }
  if (typeof value === 'number') {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d.toISOString();
    return new Date().toISOString();
  }
  if (value instanceof Date) {
    if (!Number.isNaN(value.getTime())) return value.toISOString();
    return new Date().toISOString();
  }
  if (typeof value === 'object' && value !== null) {
    const obj = value as Record<string, any>;
    if (typeof obj.toDate === 'function') {
      try {
        const d = obj.toDate();
        if (d instanceof Date && !Number.isNaN(d.getTime())) return d.toISOString();
      } catch (err) {
        void err;
      }
    }
    if (typeof obj.seconds === 'number') {
      const d = new Date(obj.seconds * 1000 + (typeof obj.nanoseconds === 'number' ? obj.nanoseconds / 1000000 : 0));
      if (!Number.isNaN(d.getTime())) return d.toISOString();
    }
    if (typeof obj._seconds === 'number') {
      const d = new Date(obj._seconds * 1000);
      if (!Number.isNaN(d.getTime())) return d.toISOString();
    }
  }
  return new Date().toISOString();
}